import axios, { AxiosResponse } from "axios";

interface Config {
  clientId: string;
  clientSecret: string;
  organismSlug: string;
  baseUrl: string;
  errorUrl: string;
  successUrl: string;
}

interface Token {
  access_token: string;
  [key: string]: any;
}

interface CartData {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  teamName: string;
  method: number;
  amount: number;
}

interface CheckoutIntent {
  [key: string]: any;
}

class HelloAssoApiWrapper {
  private config: Config;
  private token: Token | null;

  constructor(config: Config) {
    this.config = config;
    this.token = null;
    this.initToken(config.clientId, config.clientSecret);
  }

  private async initToken(
    clientId: string,
    clientSecret: string
  ): Promise<void> {
    try {
      const response: AxiosResponse = await axios.post(
        `${config.apiUrl}/oauth2/token`,
        new URLSearchParams({
          grant_type: "client_credentials",
          client_id: clientId,
          client_secret: clientSecret,
        }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      if (response.status === 200) {
        this.token = response.data;
      } else {
        console.error(
          "Unexpected HTTP status:",
          response.status,
          response.statusText
        );
      }
    } catch (error) {
      console.error("Error:", (error as Error).message);
    }
  }

  public async initCart(
    data: CartData
  ): Promise<CheckoutIntent | { error: string }> {
    try {
      let body: any = {
        totalAmount: Math.round(data.amount * 100),
        initialAmount: Math.round(data.amount * 100),
        itemName: "Inscription Tournoi",
        backUrl: `${this.config.baseUrl}?team=${data.id}`,
        errorUrl: `${this.config.errorUrl}?team=${data.id}`,
        returnUrl: `${this.config.successUrl}?team=${data.id}`,
        containsDonation: false,
        payer: {
          firstName: data.firstname,
          lastName: data.lastname,
          email: data.email,
        },
        metadata: {
          reference: data.id,
        },
      };

      if (data.method > 1) {
        body = this.manageMultiplePayment(data.method, data.amount * 100, body);
      }

      const response: AxiosResponse = await axios.post(
        `${config.apiUrl}/v5/organizations/${this.config.organismSlug}/checkout-intents`,
        body,
        {
          headers: {
            Authorization: `Bearer ${this.token?.access_token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      return { error: (error as Error).toString() };
    }
  }

  private manageMultiplePayment(
    paymentCount: number,
    totalAmount: number,
    body: any
  ): any {
    const termsAmount = Math.floor((totalAmount / paymentCount) * 100) / 100;
    const rest =
      Math.round((totalAmount - termsAmount * paymentCount) * 100) / 100;

    body.initialAmount = termsAmount;
    body.terms = [];

    const today = new Date();
    let nextPayment = new Date(today.getFullYear(), today.getMonth(), 1);

    for (let i = 1; i < paymentCount; i++) {
      nextPayment.setMonth(nextPayment.getMonth() + 1);
      body.terms.push({
        amount: i === paymentCount - 1 ? termsAmount + rest : termsAmount,
        date: nextPayment.toISOString().split("T")[0],
      });
    }

    return body;
  }
}

const config = {
  apiUrl: process.env.REACT_APP_HELLOASSO_API_URL || "",
  clientId: process.env.REACT_APP_HELLOASSO_CLIENT_ID || "",
  clientSecret: process.env.REACT_APP_HELLOASSO_CLIENT_SECRET || "",
  organismSlug: process.env.REACT_APP_HELLOASSO_ORGANISM_SLUG || "",
  baseUrl: process.env.REACT_APP_HELLOASSO_BASE_URL || "",
  errorUrl: process.env.REACT_APP_HELLOASSO_ERROR_URL || "",
  successUrl: process.env.REACT_APP_HELLOASSO_SUCCESS_URL || "",
};

export const helloAssoAPI = new HelloAssoApiWrapper(config);
